import type { SavedRecipe } from "@/hooks/useSavedRecipes";
import type { Recipe, Ingredient, MealKey, MealBlock } from "@/data/recipes";

/**
 * Convert a SavedRecipe (from the recipe calculator / DB) into the
 * internal Recipe format used by the diet generator and scaling system.
 *
 * Each ingredient's macros are computed from taco_foods data (per 100g)
 * and the stored quantity_grams.
 */
export function savedRecipeToRecipe(saved: SavedRecipe): Recipe {
  const ingredients: Ingredient[] = saved.recipe_ingredients.map((ing) => {
    const food = ing.taco_foods;
    const factor = ing.quantity_grams / 100;
    const cal = Math.round((food.energy_kcal || 0) * factor);
    const protein = Math.round((food.protein_g || 0) * factor * 10) / 10;
    const carbs = Math.round((food.carbohydrate_g || 0) * factor * 10) / 10;
    const fat = Math.round((food.lipid_g || 0) * factor * 10) / 10;

    return {
      name: food.description,
      amount: `${ing.quantity_grams}g`,
      cal,
      protein,
      carbs,
      fat,
      taco_food_id: ing.taco_food_id,
      quantity_grams: ing.quantity_grams,
      source: food.category || "TACO",
    };
  });

  const totalCal = ingredients.reduce((s, i) => s + i.cal, 0);
  const totalProtein = Math.round(ingredients.reduce((s, i) => s + i.protein, 0) * 10) / 10;

  return {
    id: `saved_${saved.id}`,
    label: saved.name,
    emoji: saved.icon || "📋",
    cal: totalCal,
    protein: totalProtein,
    ingredients,
    // No category — treated as a generic/simple item by the solver
  };
}

/**
 * Merge saved recipes into existing recipe blocks so the diet generator
 * can find and scale them. Each meal block that has saved recipe preferences
 * will get those recipes appended to its items array.
 */
export function mergeUserRecipesIntoBlocks(
  baseRecipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  savedRecipes: SavedRecipe[],
): Record<MealKey, MealBlock> {
  // Build a map of saved recipe IDs that are actually selected
  const neededIds = new Set<string>();
  for (const ids of Object.values(preferences)) {
    for (const id of ids) {
      if (id.startsWith("saved_")) neededIds.add(id);
    }
  }

  if (neededIds.size === 0) return baseRecipes;

  // Convert needed saved recipes
  const convertedMap = new Map<string, Recipe>();
  for (const saved of savedRecipes) {
    const recipeId = `saved_${saved.id}`;
    if (neededIds.has(recipeId)) {
      convertedMap.set(recipeId, savedRecipeToRecipe(saved));
    }
  }

  if (convertedMap.size === 0) return baseRecipes;

  // Clone blocks and inject saved recipes where selected
  const result = {} as Record<MealKey, MealBlock>;
  for (const key of Object.keys(baseRecipes) as MealKey[]) {
    const block = baseRecipes[key];
    const mealPrefs = preferences[key] || [];
    const savedInMeal = mealPrefs
      .filter((id) => id.startsWith("saved_") && convertedMap.has(id))
      .map((id) => convertedMap.get(id)!);

    if (savedInMeal.length > 0) {
      result[key] = {
        ...block,
        items: [...block.items, ...savedInMeal],
      };
    } else {
      result[key] = block;
    }
  }

  return result;
}
